import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Clock, MapPin, Heart, Zap, Sparkles, Users } from "lucide-react";

/**
 * SharedRitualsFeed — a soft browse of every ritual shared by a Wellness
 * Professional across every category. Filters live in a chip row (no search
 * bar, no infrastructure), so a member can quietly narrow by Trainer, Massage,
 * Nutrition, Longevity, etc. Every card shows the professional-type as a
 * pill so the viewer knows who shared it without opening the detail page.
 */
const CHIPS = [
  { key: "all", label: "All" },
  { key: "spa", label: "Spa" },
  { key: "personal_trainer", label: "Personal Trainer" },
  { key: "fitness_movement", label: "Fitness / Movement" },
  { key: "massage_bodywork", label: "Massage / Bodywork" },
  { key: "nutrition_wellness", label: "Nutrition / Wellness" },
  { key: "longevity", label: "Longevity" },
  { key: "other", label: "Other" },
];

export const PROFESSIONAL_LABELS = CHIPS.reduce((m, c) => {
  if (c.key !== "all") m[c.key] = c.label;
  return m;
}, {});

export default function SharedRitualsFeed() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = filter === "all" ? {} : { professional_type: filter };
        const { data } = await endpoints.spaList(params);
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) setItems([]);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [filter]);

  // Rough count per category for the chip labels — matches whatever the
  // server just returned unless we're on "all".
  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((r) => { c[r.professional_type] = (c[r.professional_type] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <section className="space-y-6" data-testid="shared-rituals-feed">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
          <Users size={12} /> Shared by professionals
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-2 tracking-tight italic">What actually works.</h2>
        <p className="mt-2 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Rituals shared by trainers, therapists, and wellness professionals — filtered by category so you can
          learn from real practice, not sales copy.
        </p>
      </div>

      <div
        className="flex items-center gap-2 flex-wrap"
        data-testid="shared-rituals-filter-chips"
        role="tablist"
        aria-label="Filter shared rituals by professional type"
      >
        {CHIPS.map((c) => {
          const on = filter === c.key;
          const n = filter === "all" ? counts[c.key] : (c.key === "all" ? counts.all : (on ? counts.all : undefined));
          return (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setFilter(c.key)}
              data-testid={`shared-rituals-chip-${c.key}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border hairline tab-link inline-flex items-center gap-2 ${
                on
                  ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] border-transparent"
                  : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--sand))]"
              }`}
            >
              {c.label}
              {typeof n === "number" && (
                <span className="opacity-70 tabular-nums normal-case">{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="shared-rituals-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center rounded-md border hairline bg-[hsl(var(--paper))]" data-testid="shared-rituals-empty">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Nothing here yet</div>
          <p className="mt-2 font-display text-xl italic">
            When professionals share what works in this category, it will land here.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shared-rituals-grid">
          {items.map((r) => (
            <SharedRitualCard key={r.code} ritual={r} />
          ))}
        </div>
      )}
    </section>
  );
}

function SharedRitualCard({ ritual }) {
  const total = (ritual.steps || []).reduce((n, s) => n + (s.minutes || 0), 0);
  const label = PROFESSIONAL_LABELS[ritual.professional_type] || "Wellness Professional";
  return (
    <Link
      to={`/r/${ritual.code}`}
      data-testid={`shared-ritual-card-${ritual.code}`}
      className="group block p-5 border hairline rounded-md bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))]/40 tab-link"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))] border border-[hsl(var(--terracotta))]/25"
          data-testid={`shared-ritual-type-${ritual.code}`}
        >
          {label}
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1">
          <Clock size={10} /> {total || 5} min
        </span>
        {ritual.city && (
          <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1">
            <MapPin size={10} /> {ritual.city}
          </span>
        )}
      </div>
      <div className="mt-3 font-display text-xl leading-tight tracking-tight">{ritual.title}</div>
      <div className="mt-1.5 text-[13px] text-[hsl(var(--muted-foreground))] truncate">by {ritual.spa_name}</div>
      {ritual.intention && (
        <p className="mt-3 text-sm italic text-[hsl(var(--fg))]/80 leading-relaxed line-clamp-3">
          "{ritual.intention}"
        </p>
      )}
      <div className="mt-4 flex items-center gap-3 text-[11px] text-[hsl(var(--muted-foreground))]">
        <span className="inline-flex items-center gap-1"><Heart size={11} /> {ritual.reaction_counts?.love || 0}</span>
        <span className="inline-flex items-center gap-1"><Zap size={11} /> {ritual.reaction_counts?.wow || 0}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[hsl(var(--terracotta))] opacity-0 group-hover:opacity-100 transition-opacity">
          Read <Sparkles size={10} />
        </span>
      </div>
    </Link>
  );
}
